import { MibState } from '../../foundations';
import { MibIconName } from '../icons';

/** One tab of a row: what it opens, what it says, and what the screen adds to it. */
export interface MibTab {
  /** What this tab opens, and what `selected` holds while it is open. */
  value: string;
  /** The name on the tab. Cut rather than pushed wide when it is too long. */
  label: string;
  /** An icon before the name. */
  icon?: MibIconName;
  /** Draws the icon alone, the name becoming what the tab says on hover and to a screen reader. */
  iconOnly?: boolean;
  /** A second line under the name — what the thing is, where it comes from. */
  subLabel?: string;
  /** A dot before the name, in the colour of the state it reports. */
  dot?: MibState;
  /** What the dot means, for a reader who cannot see its colour. */
  dotLabel?: string;
  /** Draws a spinner in place of the icon or the dot while what the tab opens is being loaded. */
  loading?: boolean;
  /**
   * Where this tab leads, for a row that navigates. The tab becomes a real link: ctrl-click opens it
   * beside, and the address shows on hover. A plain click still only opens the tab, so the screen
   * keeps deciding what that means.
   */
  href?: string;
  /** Draws the cross that asks for this tab to be closed. */
  closable?: boolean;
  /**
   * `content`: as wide as what it carries, and no wider. `flex`, the default: shares the room of the
   * bar with the other flex tabs, narrowing as they open down to a floor, past which the name is cut.
   */
  fit?: MibTabFit;
  disabled?: boolean;
}

/** How a tab takes its width: from its content, or from the room the bar leaves. */
export type MibTabFit = 'content' | 'flex';

/** How the open tab is marked: a line under it, or its ink alone. */
export type MibTabMarker = 'line' | 'none';

/** How much room a flex tab asks for, which decides how many fit before the arrow. */
export type MibTabDensity = 'comfortable' | 'compact';

/** Where the row draws its vertical lines: after every tab, or once after the last one. */
export type MibTabDividers = 'each' | 'end';

/** Where a tab's `subLabel` is drawn: under its name on the row and in the panel, or in the panel only. */
export type MibTabSubLabels = 'row' | 'panel';

/* `floor` repeats the `min-w` of `bounds` as a number the row can count with: change both together. */
export const TAB_DENSITY: Record<MibTabDensity, { bounds: string; floor: number }> = {
  comfortable: { bounds: 'min-w-44 max-w-75', floor: 176 },
  compact: { bounds: 'min-w-24 max-w-50', floor: 96 },
};

/** How a row of tabs behaves under the pointer. Every key is optional; one left out keeps its default. */
export interface MibTabsBehaviour {
  /** Dragging the row, or a horizontal wheel, moves along the tabs while some are left off. */
  grab?: boolean;
  /** The middle button closes a closable tab, as in a browser, instead of opening its address beside. */
  closeOnMiddleClick?: boolean;
  /** The panel lists the `content` tabs too. Off, it lists the flex tabs only, the others staying on the row. */
  panelContentTabs?: boolean;
  /** Closable tabs are listed in the panel only, never on the row, so the arrow is drawn as soon as one is open. */
  closableInPanel?: boolean;
}

/** What a row does when `behaviour` does not say otherwise. */
export const MIB_TABS_BEHAVIOUR: Required<MibTabsBehaviour> = {
  grab: true,
  closeOnMiddleClick: true,
  panelContentTabs: true,
  closableInPanel: false,
};

/* The arrow's cell on a desktop, reserved until the row has drawn it and can read its real width. */
export const TAB_OVERFLOW_WIDTH = 50;

/** The dot a tab can carry, in the colour of the state it reports. */
export const TAB_DOT_INK: Record<MibState, string> = {
  success: 'bg-mib-success',
  warning: 'bg-mib-warning',
  danger: 'bg-mib-danger',
  info: 'bg-mib-info',
};
